import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';

export interface QuizLockedModalProps {
  show: boolean;
  onHide: () => void;
  /** Localized quiz title for display */
  quizTitle: string;
  /** Optional — e.g. navigate to /?tab=career */
  onGoCareer?: () => void;
  /**
   * <c>quiz</c> — whole quiz locked (unlockable until career mission).
   * <c>difficulty</c> — Medium/Hard band locked until career unlock; left button becomes “change difficulty”.
   */
  variant?: 'quiz' | 'difficulty';
}

/**
 * Aviso quando o utilizador tenta jogar um quiz marcado como desbloqueável (ainda bloqueado até missão no Modo Carreira).
 */
const QuizLockedModal: React.FC<QuizLockedModalProps> = ({
  show,
  onHide,
  quizTitle,
  onGoCareer,
  variant = 'quiz',
}) => {
  const { t } = useSafeTranslation();
  const isDifficulty = variant === 'difficulty';

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
          <i className="bi bi-lock-fill text-secondary" aria-hidden />
          {isDifficulty
            ? t('quiz.difficultyBlockedTitle', 'Difficulty locked')
            : t('quiz.blockedTitle', 'Quiz locked')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2 fw-semibold">{quizTitle}</p>
        <p className="text-muted mb-0 small">
          {isDifficulty
            ? t(
                'quiz.difficultyBlockedBody',
                'This difficulty is locked. Complete the matching mission in Career mode to unlock it.'
              )
            : t(
                'quiz.blockedBody',
                'This quiz is locked. Complete the matching mission in Career mode to unlock it.'
              )}
        </p>
      </Modal.Body>
      <Modal.Footer className="justify-content-between flex-wrap gap-2">
        <Button variant="outline-secondary" type="button" onClick={onHide}>
          {isDifficulty
            ? t('quiz.difficultyBlockedChangeDifficulty', 'Change difficulty')
            : t('common.close', 'Close')}
        </Button>
        {onGoCareer ? (
          <Button variant="primary" type="button" onClick={onGoCareer}>
            {t('quiz.blockedGoCareer', 'Go to Career mode')}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
};

export default QuizLockedModal;
